import React, { ReactElement } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch } from 'src/store/hooks'
import {
  updatePendingANCCEvent,
  updatePendingANCCCourse,
  fetchEventAccreditations,
  fetchCourseAccreditations,
} from 'src/v3/content-pipeline/accreditation.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { StatusField } from '../shared'
import {
  CourseCodeField,
  ExpirationDateField,
  TargetAudienceField,
  EducationalNeedsField,
  CompetencyField,
  ProfessionalSourceField,
  NotesField,
  FieldsRow,
} from './form-fields'
import { ANCCPendingAccreditation, ANCCFinalizedAccreditation } from './types'

type EditFormProps = {
  eventId?: string
  courseId?: string
  pending: ANCCPendingAccreditation
  finalized?: ANCCFinalizedAccreditation
  open: boolean
  setOpen: (isOpen: boolean) => void
  onSuccess: () => void
}

export let EditForm = ({
  eventId,
  courseId,
  pending,
  finalized,
  open,
  setOpen,
  onSuccess,
}: EditFormProps): ReactElement => {
  let dispatch = useAppDispatch()

  let form = useForm({
    defaultValues: {
      status: pending.status,
      courseCode: pending.courseCode || finalized?.courseCode || '',
      expirationDate: pending.expirationDate || finalized?.expirationDate || '',
      targetAudience:
        pending.targetAudience.length > 0
          ? pending.targetAudience
          : finalized?.targetAudience || [],
      educationalNeeds:
        pending.educationalNeeds.length > 0
          ? pending.educationalNeeds
          : finalized?.educationalNeeds || [],
      competency: pending.competency || finalized?.competency || '',
      professionalSource:
        pending.professionalSource || finalized?.professionalSource || '',
      notes: pending.notes || '',
    },
    onSubmit: ({ value }) => {
      if (eventId) {
        dispatch(
          updatePendingANCCEvent({
            id: pending.id,
            eventId,
            status: value.status,
            courseCode: value.courseCode || null,
            expirationDate: value.expirationDate || null,
            targetAudience: value.targetAudience,
            educationalNeeds: value.educationalNeeds,
            competency: value.competency || null,
            professionalSource: value.professionalSource || null,
            notes: value.notes || null,
          }),
        )
        dispatch(fetchEventAccreditations(eventId))
      } else if (courseId) {
        dispatch(
          updatePendingANCCCourse({
            id: pending.id,
            courseId,
            status: value.status,
            courseCode: value.courseCode || null,
            expirationDate: value.expirationDate || null,
            targetAudience: value.targetAudience,
            educationalNeeds: value.educationalNeeds,
            competency: value.competency || null,
            professionalSource: value.professionalSource || null,
            notes: value.notes || null,
          }),
        )
        dispatch(fetchCourseAccreditations(courseId))
      }
      setOpen(false)
      onSuccess()
    },
  })

  let handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.handleSubmit()
  }

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <form onSubmit={handleFormSubmit}>
        <DialogTitle>Edit Pending ANCC Accreditation</DialogTitle>
        <DialogContent>
          <Box className="grid gap-6 mt-2">
            <Typography color="text.secondary" variant="body2">
              Update the pending ANCC accreditation details.
            </Typography>

            <form.Field
              name="status"
              children={(field) => <StatusField field={field} />}
            />

            <form.Field
              name="courseCode"
              children={(field) => <CourseCodeField field={field} />}
            />

            <form.Field
              name="expirationDate"
              children={(field) => <ExpirationDateField field={field} />}
            />

            <FieldsRow>
              <form.Field
                name={'targetAudience' as any}
                children={(field) => <TargetAudienceField field={field} />}
              />
              <form.Field
                name={'educationalNeeds' as any}
                children={(field) => <EducationalNeedsField field={field} />}
              />
            </FieldsRow>

            <form.Field
              name="competency"
              children={(field) => <CompetencyField field={field} />}
            />

            <form.Field
              name="professionalSource"
              children={(field) => <ProfessionalSourceField field={field} />}
            />

            <form.Field
              name="notes"
              children={(field) => <NotesField field={field} />}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} type="button">
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
